import type { AxiosInstance } from 'axios'

import type {
  ShopifyCompletionCommand,
  ShopifyCompletionDto,
  ShopifyStoreReturn,
} from '../models/callback.types'

import { ApiError, appHttp, unwrap, unwrapEnvelope, type ApiEnvelope } from '@/shared/http'

/** Flat, unenveloped: `{ install_url }` on 200, `{ error }` on 4xx (BE `oauth/views.py::shopify_install`). */
interface ShopifyInstallDto {
  install_url?: unknown
}

export class ShopifyApi {
  constructor(private readonly http: AxiosInstance = appHttp) {}

  /**
   * The Shopify authorize URL, ISSUED BY THE BACKEND for this user and workspace.
   *
   * It is fetched rather than navigated to because the endpoint authenticates the caller: a
   * top-level navigation carries no bearer header, so the browser's own hop landed anonymous.
   * The caller navigates to what comes back.
   */
  async installUrl(shop: string, workspaceId: number | string): Promise<string> {
    const response = await this.http.get<ShopifyInstallDto>('/oauth/shopify/install', {
      params: { shop, workspace_id: workspaceId },
      suppressGlobalErrorHandler: true,
      meta: { suppressGlobalErrorHandler: true },
    })
    const installUrl = unwrap(response).install_url
    if (typeof installUrl !== 'string' || installUrl === '') {
      throw new ApiError({
        kind: 'unknown',
        status: null,
        code: 'SHOPIFY_INSTALL_URL_MALFORMED',
        fields: {},
        payload: response.data,
        userVisible: true,
        detail: 'Malformed Shopify install response',
      })
    }
    return installUrl
  }

  /**
   * Register the already-bound store as a workspace integration (ORP-20383).
   *
   * `IntegrationView.post` under `integration_type='shopify_store'` is what writes the
   * `WorkspaceIntegrationAccount` row and calls `process_onboarding_raw_data` — the only Shopify
   * ingestion kick-off in the backend (`common/signals.py`'s sibling call is GA4-only). The OAuth
   * return does neither, so without this call a connected store never ingests. The body is v3's
   * (`router/index.js:174-181`) because the endpoint's payload contract is unchanged.
   */
  async installStore({ workspaceId, storeId }: ShopifyStoreReturn): Promise<void> {
    await this.http.post(
      `/integrations/${workspaceId}/shopify-install/`,
      { id: storeId, workspace: workspaceId },
      { suppressGlobalErrorHandler: true, meta: { suppressGlobalErrorHandler: true } },
    )
  }

  async complete(command: ShopifyCompletionCommand): Promise<ShopifyCompletionDto> {
    const response = await this.http.post<ApiEnvelope<ShopifyCompletionDto>>(
      '/integrations/shopify/complete/',
      {
        shopify_context: command.shopifyContext,
        workspace_id: command.workspaceId,
      },
      {
        headers: { 'Idempotency-Key': command.idempotencyKey },
        suppressGlobalErrorHandler: true,
        meta: { suppressGlobalErrorHandler: true },
      },
    )
    return unwrapEnvelope(response)
  }
}
